import {Form, Spin} from 'antd';
import ui from '../collectionsUI.module.scss';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {
  UploadDocumentToBucket,
  UploadVideoToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {useMutation, useQuery} from '@apollo/client';
import {UPDATE_COLLECTION} from 'graphql/mutations/collections';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {onTranslateCollection} from './actions';
import {useNavigate, useParams} from 'react-router-dom';
import backIcon from 'images/back.png';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {
  GET_ALL_COLLECTIONS,
  GET_COLLECTION_BY_ID,
} from 'graphql/queries/collections';
import {GeneralInfo} from './CollectionTabs/forms/general';
import {FinancialInfo} from './CollectionTabs/forms/financial';
import {
  membersToRows,
  toInstrumentsInput,
  validateConstituents,
} from './CollectionTabs/forms/CollectionConstituents';
import {DabaScore} from './CollectionTabs/forms/dabaScore';

const WIZARD_STEPS = [
  {key: 'GENERAL', name: 'Identity', hint: 'Name, media, description'},
  {key: 'FINANCIAL', name: 'Market', hint: 'Exchange and constituents'},
  {key: 'DABA_SCORE', name: 'Score & publish', hint: 'Daba score, status'},
];

/* Every step is reachable when editing — the record already exists, so there
   is nothing to complete in order. */
const StepRail = ({activeTab, onTabChange}) => (
  <nav className={ui.steps}>
    {WIZARD_STEPS.map((step, index) => {
      const isActive = activeTab === step.key;
      return (
        <button
          key={step.key}
          type="button"
          className={`${ui.step} ${isActive ? ui.stepActive : ''}`}
          onClick={() => onTabChange(step.key)}>
          <span
            className={`${ui.stepMark} ${isActive ? ui.stepMarkActive : ''}`}>
            {index + 1}
          </span>
          <span className={ui.stepText}>
            <span className={ui.stepName}>{step.name}</span>
            <span className={ui.stepHint}>{step.hint}</span>
          </span>
        </button>
      );
    })}
  </nav>
);

export const EditCollection = () => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [firstForm] = Form.useForm();
  const [secondForm] = Form.useForm();
  const [thirdForm] = Form.useForm();
  const announce = Form.useWatch('announce', thirdForm) === true;
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [imgUrl, setImgUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [videoUploadLoading, setVideoUploadLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [constituents, setConstituents] = useState([{}]);
  const [reportName, setReportName] = useState('Upload PDF Report');
  const [viewPdf, setViewPdf] = useState('');
  const [formValues, setFormValues] = useState({});
  const [pdfReport, setPdfReport] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] =
    useState('');
  const [translatedCollections, setTranslatedCollections] = useState([]);
  const [allowUsers, setAllowUsers] = useState(false);
  const [tradeable, setTradeable] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState('');

  const {id} = useParams();

  const {loading, refetch, data} = useQuery(GET_COLLECTION_BY_ID, {
    variables: {
      collectionId: id,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error?.message),
  });

  useEffect(() => {
    if (data?.getCollection?.data) {
      setTranslatedCollections(
        data?.getCollection?.data?.multilang?.map(data => ({
          imageUrl: data.bannerUrl,
          previewUrl: data.bannerUrl,
          description: data.description,
          name: data.name,
          pdfReport: data.pdfReport,
          viewPdf: data.pdfReport,
          videoFile: data.videoUrl,
          language: data.language,
        })),
      );
      setImgUrl(data?.getCollection?.data?.bannerUrl);
      setVideoUrl(data?.getCollection?.data?.videoUrl);
      setPreviewUrl(data?.getCollection?.data?.bannerUrl);
      setSelectedIcon(data?.getCollection?.data?.iconUrl);
      // Every existing collection predates Instrument v2 and stores null for
      // this, yet the resolver requires a boolean on write. Default to false —
      // the safer of the two, since it keeps the basket intact.
      setAllowUsers(
        data?.getCollection?.data?.allowUserRemoveInstrument ?? false,
      );
      // Collections saved before this setting have none, and those stay open.
      setTradeable(data?.getCollection?.data?.tradeable ?? true);
      // `members` is the only source of the configured unit rules; the
      // `instruments` array carries the instruments without them.
      setConstituents(membersToRows(data?.getCollection?.data?.members));
      setSelectedExchange(
        `${data?.getCollection?.data?.stockExchange}~${
          data?.getCollection?.data?.exchange?.code ?? ''
        }`,
      );
      setPdfReport(data?.getCollection?.data?.dabaScore?.pdfReport);
      firstForm.setFieldsValue({
        language: data?.getCollection?.data?.language,
        translation: data?.getCollection?.data?.multilang[0]?.language || '',
        collectionName: data?.getCollection?.data?.name,
        collectionDescription: data?.getCollection?.data?.description,
        iconUrl: data?.getCollection?.data?.iconUrl,
        industry: data?.getCollection?.data?.industry?.id,
        stockExchange: data?.getCollection?.data?.stockExchange,
      });
      secondForm.setFieldsValue({
        currency: data?.getCollection?.data?.currency,
        orderType: data?.getCollection?.data?.orderType,
        ticker: data?.getCollection?.data?.ticker,
        minInstrumentCount: data?.getCollection?.data?.minInstrumentCount,
        // Was hardcoded to `~BRVM`, which mislabelled every collection on any
        // other exchange and reset it to BRVM on save.
        stockExchange: `${data?.getCollection?.data?.stockExchange}~${
          data?.getCollection?.data?.exchange?.code ?? ''
        }`,
        linkedCollectionGroups:
          data?.getCollection?.data?.linkedCollectionGroups?.map(
            data => data?.id,
          ),
      });
      thirdForm.setFieldsValue({
        Accelerator: data?.getCollection?.data?.dabaScore?.accelerator,
        Analogue: data?.getCollection?.data?.dabaScore?.analogus,
        business: data?.getCollection?.data?.dabaScore?.business,
        Economics: data?.getCollection?.data?.dabaScore?.economicsAndFinance,
        IndustryScore: data?.getCollection?.data?.dabaScore?.industry,
        Legal: data?.getCollection?.data?.dabaScore?.legalCompliance,
        Market: data?.getCollection?.data?.dabaScore?.market,
        Returns: data?.getCollection?.data?.dabaScore?.returnOutlook,
        Risks: data?.getCollection?.data?.dabaScore?.riskChallenges,
        dabaScore: data?.getCollection?.data?.dabaScore?.score,
        Social: data?.getCollection?.data?.dabaScore?.socialImpact,
        Strategy: data?.getCollection?.data?.dabaScore?.strategyProduct,
        Team: data?.getCollection?.data?.dabaScore?.team,
        status: data?.getCollection?.data?.status === 'Active',
      });
    }
  }, [data]);

  const navigate = useNavigate();

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );

  const handleTranslation = async (name, description, language) => {
    const translateProps = {
      sourceLang: language,
      targetLang: selectedTranslationLanguage,
      targetText1: name,
      targetText2: description,
    };
    try {
      const newValues = [...translatedCollections];

      //Check if translation exists
      if (newValues.length && newValues[0]) {
        // Check if we're trying to translate in the same language
        const languageExists = newValues.some(
          data => data.language === selectedTranslationLanguage,
        );
        if (languageExists) {
          const updateTranslation = await onTranslateCollection(
            translateText,
            translateProps,
          );
          const translationIndex = newValues.findIndex(
            data => data.language === selectedTranslationLanguage,
          );
          newValues[translationIndex].name = updateTranslation.name;
          newValues[translationIndex].description =
            updateTranslation.description;
          newValues[translationIndex].language = updateTranslation.language;
          newValues[translationIndex].videoFile = '';
          newValues[translationIndex].loadingVideoUpload = false;
          newValues[translationIndex].videoProgress = 0;
          newValues[translationIndex].imageUrl = '';
          newValues[translationIndex].previewUrl = '';
          newValues[translationIndex].pdfname = 'Supported formats pdf, docs';
          newValues[translationIndex].viewPdf = '';
          newValues[translationIndex].pdfReport = '';
          newValues[translationIndex].pdfLoading = false;
          setTranslatedCollections(newValues);
        }
      } else {
        const triggerTranslation = await onTranslateCollection(
          translateText,
          translateProps,
        );
        if (!triggerTranslation) {
          return toast.error('Something went wrong when Translating');
        }
        newValues.push(triggerTranslation);
        setTranslatedCollections(newValues);
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
  };

  const onTabChange = e => {
    setActiveTab(e);
  };

  const handleUploadVideo = async e => {
    const selectedFile = e.target.files[0];
    if (selectedFile.type === 'video/mp4') {
      setVideoUploadLoading(true);
      const response = await UploadVideoToBucket(
        selectedFile,
        'annoucement-images',
        setProgress,
      );
      setVideoUrl(response);
      setVideoUploadLoading(false);
      setProgress(0);
    } else {
      toast.error('Please upload a mp4 video');
    }
  };

  const handlePdfFileChange = async e => {
    const selectedFile = e.target.files[0];
    const fileType = ['application/pdf'];
    setReportName(selectedFile.name);
    if (selectedFile) {
      if (selectedFile && fileType.includes(selectedFile.type)) {
        // Upload file to S3 bucket
        setPdfLoading(true);
        const res = await UploadDocumentToBucket(selectedFile);
        setPdfReport(res);
        setPdfLoading(false);
      } else {
        setPdfReport(null);
        toast.error('Please select a valid pdf file');
      }
    } else {
      toast.error('Select a PDF file');
    }
  };

  const handleDeleteFile = () => {
    setPdfReport(null);
    setReportName('Supported formats pdf, docs');
    setViewPdf(null);
  };

  const handleFirstFormSubmit = values => {
    setFormValues({...formValues, ...values});
    onTabChange('FINANCIAL');
  };

  const handleSecondFormSubmit = values => {
    // The resolver reports every constituent failure as "_schema.Error is not
    // a constructor", so these rules have to hold before the request is sent.
    const problems = validateConstituents(constituents, {
      allowRemoval: allowUsers,
      minRetained: values?.minInstrumentCount,
    });
    if (problems.length) return toast.error(problems[0]);

    setFormValues({...formValues, ...values});
    onTabChange('DABA_SCORE');
  };

  const [updateCollection, {loading: loadingUpdate}] =
    useMutation(UPDATE_COLLECTION);

  const handleSave = () => {
    if (loadingUpdate) return;
    const values = thirdForm.getFieldsValue();
    const {
      name,
      description,
      ticker,
      industry,
      stockExchange,
      orderType,
      language,
      currency,
    } = data?.getCollection?.data;

    updateCollection({
      variables: {
        collectionId: id,
        input: {
          // Instrument v2: `companies`, `allowUserRemoveCompany` and
          // `minCompanyCount` are rejected by the resolver.
          allowUserRemoveInstrument: allowUsers,
          tradeable,
          bannerUrl: imgUrl,
          instruments: toInstrumentsInput(constituents),
          // Was pinned to XOF, which mislabelled every collection on a venue
          // that does not trade in CFA. Chosen on the Market step and defaulted
          // from the exchange's own base currency.
          currency: formValues?.currency || currency,
          ticker: formValues?.ticker || ticker,
          ...(values.dabaScore &&
            values.socialImpact && {
              dabaScore: {
                accelerator: Number(values?.Accelerator),
                analogus: Number(values?.Analogue),
                business: Number(values?.business),
                economicsAndFinance: Number(values?.Economics),
                industry: Number(values?.IndustryScore),
                legalCompliance: Number(values?.Legal),
                market: Number(values?.Market),
                pdfReport: pdfReport,
                returnOutlook: Number(values?.Returns),
                riskChallenges: Number(values?.Risks),
                score: Number(values?.dabaScore),
                socialImpact: Number(values?.Social),
                strategyProduct: Number(values?.Strategy),
                team: Number(values?.Team),
              },
            }),
          description: formValues?.collectionDescription || description,
          iconUrl: selectedIcon,
          linkedCollectionGroups: formValues?.linkedCollectionGroups,
          ...(allowUsers &&
            formValues?.minInstrumentCount && {
              minInstrumentCount: Number(formValues.minInstrumentCount),
            }),
          industry: formValues?.industry || industry?.id,
          language: formValues?.language || language,
          // The multilang model requires a banner even though the schema
          // marks it nullable; fall back to the base banner.
          multilang: translatedCollections.map(data => ({
            bannerUrl: data.imageUrl || imgUrl,
            description: data.description,
            name: data.name,
            pdfReport: data.pdfReport,
            videoUrl: data.videoFile,
            language: data.language,
          })),
          name: formValues?.collectionName || name,
          orderType: formValues?.orderType || orderType,
          status: values?.status ? 'Active' : 'InActive',
          announce: values?.status === true && values?.announce === true,
          stockExchange: selectedExchange?.split('~')[0] || stockExchange,
          ...(videoUrl && {videoUrl: videoUrl}),
        },
      },
      refetchQueries: [
        {query: GET_ALL_COLLECTIONS, variables: {filters: {paginate: true}}},
      ],
    })
      .then(({data}) => {
        if (data.updateCollection.__typename === 'Error') {
          toast.error(data?.updateCollection?.message);
        } else {
          refetch({
            collectionId: id,
          });
          toast.success('Collection updated successfully');
          navigate('/dashboard/collections');
        }
      })
      .catch(() => {
        toast.error(`Something went wrong`);
      });
  };

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={ui.page}>
      <button
        type="button"
        className={ui.back}
        onClick={() => navigate('/dashboard/collections')}>
        <img src={backIcon} alt="" />
        All collections
      </button>

      <div className={ui.pageHead}>
        <div>
          <h1>{data?.getCollection?.data?.name ?? 'Edit collection'}</h1>
          <p>
            Update is a full submission — every required field is re-sent, not
            only what changed.
          </p>
        </div>
      </div>

      <div className={ui.wizard}>
        <StepRail activeTab={activeTab} onTabChange={onTabChange} />
        <div className={ui.wizardBody}>
          {activeTab === 'GENERAL' && (
            <div>
              <GeneralInfo
                firstForm={firstForm}
                handleFirstFormSubmit={handleFirstFormSubmit}
                previewUrl={previewUrl}
                setImgUrl={setImgUrl}
                selectedIcon={selectedIcon}
                handleTranslation={handleTranslation}
                translatedCollections={translatedCollections}
                setTranslatedCollections={setTranslatedCollections}
                videoUploadLoading={videoUploadLoading}
                videoUrl={videoUrl}
                setVideoUrl={setVideoUrl}
                setSelectedIcon={setSelectedIcon}
                progress={progress}
                handleUploadVideo={handleUploadVideo}
                loadingTranslation={loadingTranslation}
                setSelectedTranslationLanguage={setSelectedTranslationLanguage}
                selectedTranslationLanguage={selectedTranslationLanguage}
                setPreviewUrl={setPreviewUrl}
              />
            </div>
          )}
          {activeTab === 'FINANCIAL' && (
            <div>
              <FinancialInfo
                secondForm={secondForm}
                handleSecondFormSubmit={handleSecondFormSubmit}
                constituents={constituents}
                setConstituents={setConstituents}
                selectedExchange={selectedExchange}
                setSelectedExchange={setSelectedExchange}
                allowUsers={allowUsers}
                tradeable={tradeable}
                setTradeable={setTradeable}
                setAllowUsers={setAllowUsers}
                onTabChange={onTabChange}
              />
            </div>
          )}
          {activeTab === 'DABA_SCORE' && (
            <div>
              <DabaScore
                thirdForm={thirdForm}
                announcedAt={data?.getCollection?.data?.announcedAt}
                setOpenConfirmModal={setOpenConfirmModal}
                handleDeleteFile={handleDeleteFile}
                handlePdfFileChange={handlePdfFileChange}
                onTabChange={onTabChange}
                viewPdf={viewPdf}
                pdfLoading={pdfLoading}
                pdfReport={pdfReport}
                reportName={reportName}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        title={
          announce
            ? data?.getCollection?.data?.announcedAt
              ? 'Save and announce again?'
              : 'Save and announce collection'
            : 'Update collection'
        }
        loading={loadingUpdate}
        confirmText={
          announce
            ? `Save and announce ${
                formValues?.collectionName ||
                data?.getCollection?.data?.name ||
                'this collection'
              }${
                data?.getCollection?.data?.announcedAt ? ' again' : ''
              } to all users? This sends a push and posts to social channels.`
            : 'Save these changes? Investors see the updated collection immediately.'
        }
        confirmation={true}
        handleOperation={handleSave}
      />
    </div>
  );
};
